import React, { useState, useEffect } from 'react';
import { useGame } from '../context/GameContext';
import { ArrowLeft, Volume2, VolumeX, Radio, Bug, Eye, Keyboard, RotateCcw, Music, Download, Upload, Maximize, Minimize, Cloud } from 'lucide-react';
import { setMasterVolume, setMusicVolume, setSfxVolume, getVolumes, sfx } from '../game/sound';
import { isSteamActive } from '../game/steam';

const CB_MODES = [
  { key: 'none', label: 'OFF' },
  { key: 'protanopia', label: 'PROTANOPIA' },
  { key: 'deuteranopia', label: 'DEUTERANOPIA' },
  { key: 'tritanopia', label: 'TRITANOPIA' },
  { key: 'highcontrast', label: 'HIGH CONTRAST' },
];

const DEFAULT_KEYBINDS = {
  endTurn: 'e',
  undo: 'u',
  toggleLog: 'z',
  openDeck: 'd',
  openMap: 'm',
  pause: ' ',
  cheatsheet: '?',
};
const BIND_LABELS = {
  endTurn: 'END TURN',
  undo: 'UNDO CARD',
  toggleLog: 'BATTLE LOG',
  openDeck: 'DECK VIEWER',
  openMap: 'MAP',
  pause: 'PAUSE',
  cheatsheet: 'HOTKEY SHEET',
};

export default function Settings() {
  const { setScreen, muted, toggleMute, streamerMode, toggleStreamerMode, discardSavedRun, hasSavedRun } = useGame();

  const [reduceMotion, setReduceMotion] = useState(() => {
    try { return localStorage.getItem('friendslop.reduceMotion') === '1'; } catch (e) { return false; }
  });
  const [shake, setShake] = useState(() => {
    try { return localStorage.getItem('friendslop.shake') !== '0'; } catch (e) { return true; }
  });
  const [animSpeed, setAnimSpeed] = useState(() => {
    try { return localStorage.getItem('friendslop.animSpeed') || '1'; } catch (e) { return '1'; }
  });
  // v2.8 QoL #7: auto-end turn when hand is empty or no playable cards
  const [autoEnd, setAutoEnd] = useState(() => {
    try { return localStorage.getItem('friendslop.autoEnd') === '1'; } catch (e) { return false; }
  });
  const applyAutoEnd = (v) => {
    setAutoEnd(v);
    try { localStorage.setItem('friendslop.autoEnd', v ? '1' : '0'); } catch (e) {}
  };

  // v2.6 volumes
  const [vol, setVol] = useState(() => getVolumes());
  // v2.6 colorblind
  const [cb, setCb] = useState(() => {
    try { return localStorage.getItem('friendslop.cbMode') || 'none'; } catch (e) { return 'none'; }
  });
  // v2.6 custom keybinds
  const [binds, setBinds] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('friendslop.keybinds') || '{}');
      return { ...DEFAULT_KEYBINDS, ...stored };
    } catch (e) { return { ...DEFAULT_KEYBINDS }; }
  });
  const [rebinding, setRebinding] = useState(null);

  // Apply CB mode class + persist
  useEffect(() => {
    document.body.classList.remove('cb-protanopia','cb-deuteranopia','cb-tritanopia','cb-highcontrast');
    if (cb !== 'none') document.body.classList.add(`cb-${cb}`);
    try { localStorage.setItem('friendslop.cbMode', cb); } catch (e) {}
  }, [cb]);

  // Rebind capture
  useEffect(() => {
    if (!rebinding) return;
    const onKey = (e) => {
      e.preventDefault();
      if (e.key === 'Escape') { setRebinding(null); return; }
      const newBinds = { ...binds, [rebinding]: e.key };
      setBinds(newBinds);
      try { localStorage.setItem('friendslop.keybinds', JSON.stringify(newBinds)); } catch (err) {}
      setRebinding(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rebinding, binds]);

  const resetBinds = () => {
    setBinds({ ...DEFAULT_KEYBINDS });
    try { localStorage.setItem('friendslop.keybinds', JSON.stringify(DEFAULT_KEYBINDS)); } catch (e) {}
  };

  const applyAnimSpeed = (v) => {
    setAnimSpeed(v);
    try { localStorage.setItem('friendslop.animSpeed', v); } catch (e) {}
    document.body.classList.remove('speed-1', 'speed-2', 'speed-instant');
    document.body.classList.add(`speed-${v === '1' ? '1' : v === '2' ? '2' : 'instant'}`);
  };
  useEffect(() => { applyAnimSpeed(animSpeed); /* eslint-disable-next-line */ }, []);
  const cycleAnimSpeed = () => {
    const next = animSpeed === '1' ? '2' : animSpeed === '2' ? 'instant' : '1';
    applyAnimSpeed(next);
  };
  const applyReduceMotion = (v) => {
    setReduceMotion(v);
    try { localStorage.setItem('friendslop.reduceMotion', v ? '1' : '0'); } catch (e) {}
    document.body.classList.toggle('reduce-motion', v);
  };
  const applyShake = (v) => {
    setShake(v);
    try { localStorage.setItem('friendslop.shake', v ? '1' : '0'); } catch (e) {}
    document.body.classList.toggle('no-shake', !v);
  };
  const clearAllData = () => {
    if (!window.confirm('Clear ALL local save data (run + settings + tag)? This cannot be undone.')) return;
    try {
      Object.keys(localStorage).forEach(k => {
        if (k.startsWith('friendslop.')) localStorage.removeItem(k);
      });
    } catch (e) {}
    window.location.reload();
  };
  const applyVol = (kind, v) => {
    setVol(o => ({ ...o, [kind]: v }));
    if (kind === 'master') setMasterVolume(v);
    if (kind === 'music') setMusicVolume(v);
    if (kind === 'sfx') { setSfxVolume(v); try { sfx.click && sfx.click(); } catch (e) {} }
  };

  // v3.17 STEAM-READY: fullscreen toggle + save export / import + steam status
  const [isFullscreen, setIsFullscreen] = useState(() => !!document.fullscreenElement);
  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);
  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        localStorage.setItem('friendslop.fullscreen', '1');
      } else {
        await document.exitFullscreen();
        localStorage.setItem('friendslop.fullscreen', '0');
      }
    } catch (e) {}
  };

  const exportSave = () => {
    try {
      const data = {};
      Object.keys(localStorage).forEach(k => {
        if (k.startsWith('friendslop.')) data[k] = localStorage.getItem(k);
      });
      const payload = {
        version: '3.17',
        gameId: 'friendslop-gooning-time',
        exportedAt: new Date().toISOString(),
        data,
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `friendslop-save-${Date.now()}.json`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
    } catch (e) {
      window.alert('Failed to export save: ' + (e && e.message || 'unknown'));
    }
  };

  const importSave = (evt) => {
    const file = evt.target.files && evt.target.files[0];
    if (!file) return;
    if (!window.confirm('Importing a save will OVERWRITE your current progress. Continue?')) {
      evt.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        if (!parsed || !parsed.data || parsed.gameId !== 'friendslop-gooning-time') {
          throw new Error('Not a valid Friend Slop save file.');
        }
        // Clear existing friendslop.* keys before restore
        Object.keys(localStorage).forEach(k => { if (k.startsWith('friendslop.')) localStorage.removeItem(k); });
        Object.entries(parsed.data).forEach(([k, v]) => {
          if (k.startsWith('friendslop.')) localStorage.setItem(k, v);
        });
        window.alert('Save imported. Reloading...');
        window.location.reload();
      } catch (e) {
        window.alert('Import failed: ' + (e && e.message || 'unknown'));
      } finally {
        evt.target.value = '';
      }
    };
    reader.readAsText(file);
  };

  const kbLabel = (k) => (k === ' ' ? 'SPACE' : (k || '').toUpperCase());

  return (
    <div className="min-h-screen w-full px-6 py-6" data-testid="settings-screen">
      <div className="flex items-center justify-between">
        <button className="brutal-btn text-sm" onClick={() => setScreen('menu')} data-testid="settings-back">
          <ArrowLeft size={16}/> BACK
        </button>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase text-[color:var(--neon)]">SETTINGS</h1>
        <div className="w-24"/>
      </div>

      <div className="mt-8 max-w-2xl mx-auto space-y-6">
        {/* AUDIO */}
        <section data-testid="settings-audio-section">
          <div className="text-xs uppercase tracking-widest text-[color:var(--pink)] mb-2 flex items-center gap-2"><Music size={12}/> AUDIO</div>
          <SettingRow label="AUDIO" value={muted ? 'MUTED' : 'ON'} onToggle={toggleMute} icon={muted ? <VolumeX size={16}/> : <Volume2 size={16}/>} testid="setting-audio"/>
          <VolumeRow label="MASTER" value={vol.master} onChange={(v) => applyVol('master', v)} testid="setting-vol-master"/>
          <VolumeRow label="MUSIC" value={vol.music} onChange={(v) => applyVol('music', v)} testid="setting-vol-music"/>
          <VolumeRow label="SFX" value={vol.sfx} onChange={(v) => applyVol('sfx', v)} testid="setting-vol-sfx"/>
        </section>

        {/* VISUALS */}
        <section data-testid="settings-visual-section">
          <div className="text-xs uppercase tracking-widest text-[color:var(--pink)] mb-2 flex items-center gap-2"><Eye size={12}/> VISUAL</div>
          <SettingRow label="FULLSCREEN" value={isFullscreen ? 'ON' : 'OFF'} onToggle={toggleFullscreen} icon={isFullscreen ? <Minimize size={16}/> : <Maximize size={16}/>} testid="setting-fullscreen"/>
          <SettingRow label="STREAMER MODE" value={streamerMode ? 'ON' : 'OFF'} onToggle={toggleStreamerMode} icon={<Radio size={16}/>} testid="setting-streamer"/>
          <SettingRow label="SCREEN SHAKE" value={shake ? 'ON' : 'OFF'} onToggle={() => applyShake(!shake)} testid="setting-shake"/>
          <SettingRow label="REDUCE MOTION" value={reduceMotion ? 'ON' : 'OFF'} onToggle={() => applyReduceMotion(!reduceMotion)} testid="setting-motion"/>
          <SettingRow label="ANIMATION SPEED" value={animSpeed === '1' ? '1× (NORMAL)' : animSpeed === '2' ? '2× (FAST)' : 'INSTANT'} onToggle={cycleAnimSpeed} testid="setting-anim-speed"/>
          <SettingRow label="AUTO-END TURN" value={autoEnd ? 'ON' : 'OFF'} onToggle={() => applyAutoEnd(!autoEnd)} testid="setting-auto-end"/>
          <div className="brutal-border w-full p-4 bg-[#0a0a0a] flex items-center justify-between" data-testid="setting-colorblind">
            <div className="flex items-center gap-3">
              <span className="text-[color:var(--neon)]"><Eye size={16}/></span>
              <span className="font-display font-black uppercase">COLORBLIND MODE</span>
            </div>
            <select value={cb} onChange={(e) => setCb(e.target.value)} className="bg-[#050505] border-2 border-[color:var(--pink)] px-3 py-1 text-white font-mono uppercase text-sm" data-testid="setting-cb-select">
              {CB_MODES.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>
        </section>

        {/* KEYBINDS */}
        <section data-testid="settings-keybinds-section">
          <div className="text-xs uppercase tracking-widest text-[color:var(--pink)] mb-2 flex items-center gap-2"><Keyboard size={12}/> KEYBINDS</div>
          <div className="brutal-border w-full bg-[#0a0a0a]">
            {Object.keys(DEFAULT_KEYBINDS).map(k => (
              <div key={k} className="flex items-center justify-between px-4 py-2 border-b border-[#222] last:border-0" data-testid={`keybind-row-${k}`}>
                <span className="text-sm font-display font-black uppercase">{BIND_LABELS[k] || k}</span>
                <button
                  className="brutal-btn text-xs px-3 py-1 min-w-[80px]"
                  onClick={() => setRebinding(k)}
                  data-testid={`keybind-btn-${k}`}
                  style={rebinding === k ? { borderColor: 'var(--pink)', color: 'var(--pink)', boxShadow: '2px 2px 0 var(--pink)' } : {}}
                >
                  {rebinding === k ? 'PRESS KEY...' : kbLabel(binds[k])}
                </button>
              </div>
            ))}
          </div>
          <button className="brutal-btn text-xs w-full mt-2" onClick={resetBinds} data-testid="keybind-reset">
            <RotateCcw size={12} className="inline mr-1"/> RESET KEYBINDS TO DEFAULT
          </button>
        </section>

        {/* v3.17 STEAM-READY: SAVE DATA (Export/Import for Steam Cloud + backup) */}
        <section data-testid="settings-save-section">
          <div className="text-xs uppercase tracking-widest text-[color:var(--pink)] mb-2 flex items-center gap-2"><Cloud size={12}/> SAVE DATA</div>
          <div className="brutal-border w-full p-4 bg-[#0a0a0a] flex flex-col gap-2">
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--text-dim)] font-mono">
              {isSteamActive() ? 'STEAM CLOUD ACTIVE · LOCAL BACKUP AVAILABLE' : 'LOCAL SAVE · EXPORT FOR BACKUP OR CLOUD SYNC'}
            </div>
            <button className="brutal-btn text-sm w-full flex items-center justify-center gap-2" onClick={exportSave} data-testid="export-save-btn">
              <Download size={14}/> EXPORT SAVE (.json)
            </button>
            <label className="brutal-btn text-sm w-full flex items-center justify-center gap-2 cursor-pointer" data-testid="import-save-btn">
              <Upload size={14}/> IMPORT SAVE (.json)
              <input type="file" accept="application/json,.json" onChange={importSave} className="hidden" data-testid="import-save-input"/>
            </label>
          </div>
        </section>

        {/* DANGER ZONE */}
        <section data-testid="settings-danger-section">
          <div className="text-xs uppercase tracking-widest text-[color:var(--red)] mb-2">DANGER ZONE</div>
          {hasSavedRun && (
            <button className="brutal-btn w-full text-sm text-[color:var(--red)] mb-2" onClick={discardSavedRun} data-testid="clear-save-btn">
              DISCARD SAVED RUN
            </button>
          )}
          <button className="brutal-btn w-full text-sm text-[color:var(--red)]" onClick={clearAllData} data-testid="clear-all-btn">
            CLEAR ALL LOCAL DATA (RESET)
          </button>
        </section>

        <button
          className="brutal-btn w-full text-sm"
          onClick={() => { try { window.__slop_openBugReporter && window.__slop_openBugReporter(); } catch (e) {} }}
          data-testid="report-bug-btn"
        >
          <Bug size={14} className="inline mr-2"/> REPORT A BUG (SHIFT+B)
        </button>
        <button className="brutal-btn w-full text-sm" onClick={() => setScreen('credits')} data-testid="credits-btn">
          CREDITS
        </button>
      </div>
    </div>
  );
}

function SettingRow({ label, value, onToggle, icon, testid }) {
  return (
    <button className="brutal-border w-full p-4 bg-[#0a0a0a] flex items-center justify-between hover:bg-[#111] transition-colors mb-2" onClick={onToggle} data-testid={testid}>
      <div className="flex items-center gap-3">
        {icon && <span className="text-[color:var(--neon)]">{icon}</span>}
        <span className="font-display font-black uppercase">{label}</span>
      </div>
      <span className="font-mono text-[color:var(--pink)]">{value}</span>
    </button>
  );
}

function VolumeRow({ label, value, onChange, testid }) {
  return (
    <div className="brutal-border w-full p-3 bg-[#0a0a0a] flex items-center gap-4 mb-2" data-testid={testid}>
      <span className="font-display font-black uppercase text-sm w-20">{label}</span>
      <input
        type="range"
        min="0"
        max="1"
        step="0.05"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="flex-1"
        data-testid={`${testid}-slider`}
      />
      <span className="font-mono text-[color:var(--neon)] w-10 text-right text-sm">{Math.round(value * 100)}</span>
    </div>
  );
}
